<template>
  <form class="questionnaire__webform webform-submission-form" @submit.prevent="submitForm">
    <div v-if="!loading" class="questionnaire__webform-fields">
      <div
        v-for="(field, key) in visibleFields"
        :key="key"
        :class="['form-item', `js-form-item-${key}`, `form-item-${key}`, `js-form-type-${field['#type']}`]"
      >
        <!-- Text, email, tel -->
        <input
          v-if="['textfield', 'email', 'tel'].includes(field['#type'])"
          :type="field['#type'] === 'textfield' ? 'text' : field['#type']"
          :id="`edit-${key}`"
          :name="key"
          v-model="formData[key]"
          :placeholder="field['#placeholder'] || ''"
          :required="field['#required']"
          class="form-text"
          @input="validateForm"
        />

        <!-- Textarea -->
        <textarea
          v-else-if="field['#type'] === 'textarea'"
          :id="`edit-${key}`"
          :name="key"
          v-model="formData[key]"
          :placeholder="field['#placeholder'] || ''"
          :required="field['#required']"
          class="form-textarea"
          @input="validateForm"
        />

        <!-- Checkbox -->
         <template v-else-if="field['#type'] === 'checkbox'">
           <input
             type="checkbox"
             :id="`edit-${key}`"
             :name="key"
             v-model="formData[key]"
             :required="field['#required']"
             class="form-checkbox"
             @change="validateForm"
           />
           <div class="description" tabindex="0"
              @click="handleDescriptionClick($event, key)"
              @keydown="handleDescriptionKeydown($event, key)"
            >
             <div class="webform-element-description" v-html="field['#description']"></div>
           </div>
         </template>

        <!-- Label -->
        <label
          v-if="field['#title'] && field['#type'] !== 'checkbox'"
          :for="`edit-${key}`"
          :class="{'form-required': field['#required'], 'form__legend-label': field['#type'] === 'label'}"
        >
          {{ field['#title'] }}
        </label>
      </div>

      <!-- Hidden questionnaire answers -->
      <input type="hidden" name="question_answers" :value="JSON.stringify(answers)" />
    </div>

    <div v-if="error" class="messages--error">{{ error }}</div>
  </form>
</template>

<script>
import translationStrings from '../utils/translationStrings';

export default {
  name: "QuestionnaireWebform",
  props: {
    answers: { type: Object, required: true },
    webformId: { type: String, required: true },
    savedFormData: { type: Object, default: null }
  },
  emits: ['submitted', 'form-validity-changed', 'form-data-changed'],
  data() {
    return {
      t: translationStrings,
      elements: {},
      formData: {},
      csrfToken: null,
      loading: true,
      submitting: false,
      error: null,
      success: false
    };
  },

  computed: {
    visibleFields() {
      return Object.fromEntries(
        Object.entries(this.elements).filter(
          ([key, field]) => field['#type'] !== 'hidden'
        )
      );
    },

    isFormValid() {
      const requiredFields = Object.entries(this.visibleFields)
        .filter(([key, field]) => field['#required'])
        .map(([key]) => key);

      return requiredFields.every(key => {
        const value = this.formData[key];
        if (this.elements[key]['#type'] === 'checkbox') {
          return value === true;
        }
        return value && value.toString().trim() !== '';
      });
    }
  },

  mounted() {
    this.initializeForm();
  },

  methods: {
    async initializeForm() {
      try {
        this.loading = true;
        this.error = null;

        // Fetch both fields and CSRF token in parallel
        const [fieldsResponse, csrfResponse] = await Promise.all([
          this.fetchFields(),
          this.fetchCsrfToken()
        ]);

        this.loading = false;
        this.$nextTick(() => {
          this.validateForm();
        });
      } catch (e) {
        this.error = e.message;
        this.loading = false;
      }
    },

    async fetchFields() {
      const res = await fetch(`/webform_rest/${this.webformId}/fields?_format=json`);
      if (!res.ok) throw new Error("Failed to load webform fields");

      this.elements = await res.json();

      // Initialize formData: restore saved data if available, otherwise use defaults
      Object.keys(this.elements).forEach((key) => {
        if (this.savedFormData && key in this.savedFormData) {
          this.formData[key] = this.savedFormData[key];
        } else {
          const defaultValue = this.elements[key]?.['#default_value'];
          if (this.elements[key]['#type'] === 'checkbox') {
            this.formData[key] = Boolean(defaultValue);
          } else {
            this.formData[key] = defaultValue || '';
          }
        }
      });
    },

    async fetchCsrfToken() {
      try {
        const res = await fetch('/session/token', {
          method: 'GET',
          credentials: 'same-origin'
        });

        if (!res.ok) {
          throw new Error(`Failed to fetch CSRF token: ${res.status}`);
        }

        this.csrfToken = await res.text();
      } catch (e) {
        console.warn('Could not fetch CSRF token:', e.message);
        // Some Drupal setups might not require CSRF for REST submissions
        // or might handle it differently, so we'll continue without it
        this.csrfToken = null;
      }
    },

    validateForm() {
      this.$emit('form-validity-changed', this.isFormValid, this.submitting);
    },

    async submitForm() {
      if (!this.isFormValid || this.submitting) {
        return;
      }

      this.submitting = true;
      this.error = null;
      this.success = false;
      this.$emit('form-validity-changed', this.isFormValid, true);

      const payload = {
        ...this.formData,
        webform_id: this.webformId,
        question_answers: JSON.stringify(this.answers)
      };

      try {
        const headers = {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        };

        // Add CSRF token if available
        if (this.csrfToken) {
          headers['X-CSRF-Token'] = this.csrfToken;
        }

        const res = await fetch(`/webform_rest/submit`, {
          method: 'POST',
          headers,
          credentials: 'same-origin',
          body: JSON.stringify(payload)
        });

        if (!res.ok) {
          // Handle CSRF token expiration
          if (res.status === 403 || res.status === 401) {
            // Try to refresh CSRF token and retry once
            await this.fetchCsrfToken();
            if (this.csrfToken) {
              return this.retrySubmission(payload);
            }
          }

          const errorData = await res.json().catch(() => ({}));
          throw new Error(errorData?.message || `Server error: ${res.status}`);
        }

        const responseData = await res.json();
        this.success = true;
        this.$emit('submitted');

      } catch (e) {
        console.error('Form submission error:', e);
        this.error = e.message || this.t.submissionFailed;
      } finally {
        this.submitting = false;
        this.$emit('form-validity-changed', this.isFormValid, false);
      }
    },

    async retrySubmission(payload) {
      const headers = {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      };

      if (this.csrfToken) {
        headers['X-CSRF-Token'] = this.csrfToken;
      }

      const res = await fetch(`/webform_rest/${this.webformId}/submit?_format=json`, {
        method: 'POST',
        headers,
        credentials: 'same-origin',
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData?.message || `Server error: ${res.status}`);
      }

      const responseData = await res.json();
      this.success = true;
      this.$emit('submitted');
    },

    handleDescriptionClick(event, fieldKey) {
      // Don't trigger if clicking on links or labels
      if (event.target.closest('a') || event.target.closest('label')) return;

      const checkbox = this.$el.querySelector(`#edit-${fieldKey}`);
      if (!checkbox || checkbox.disabled) return;

      // Toggle the checkbox
      this.formData[fieldKey] = !this.formData[fieldKey];
      this.validateForm();
    },

    handleDescriptionKeydown(event, fieldKey) {
      if (event.key === ' ' || event.key === 'Spacebar' || event.key === 'Enter') {
        event.preventDefault();

        // Don't trigger if focusing on links or labels
        if (event.target.closest('a') || event.target.closest('label')) return;

        const checkbox = this.$el.querySelector(`#edit-${fieldKey}`);
        if (!checkbox || checkbox.disabled) return;

        // Toggle the checkbox
        this.formData[fieldKey] = !this.formData[fieldKey];
        this.validateForm();
      }
    }
  },

  watch: {
    formData: {
      handler(newData) {
        this.validateForm();
        this.$emit('form-data-changed', { ...newData });
      },
      deep: true
    },

    answers: {
      handler() {
        this.validateForm();
      },
      deep: true
    }
  }
};
</script>
